import React, { useState } from "react";
import { X } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const labelClass =
  "mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-500";
const fieldClass =
  "h-11 w-full border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#C5A059]";

const titleCase = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const initialForm = {
  name: "",
  legal_name: "",
  partner_type: "law_firm",
  speciality: "real_estate_law",
  service: "legal_due_diligence",
  country: "Portugal",
  city: "",
  official_url: "",
  professional_body: "",
  registration_number: "",
  registry_url: "",
  languages: "pt,en",
  description: "",
  priority: "B",
  contact_name: "",
  job_title: "",
  email: "",
  phone: "",
  contact_source: "",
  why_relevant: "",
};

export default function LegalPartnerForm({
  meta,
  saving,
  onCancel,
  onSave,
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState(initialForm);

  const update = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const submit = (event) => {
    event.preventDefault();

    const languages = form.languages
      .split(",")
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);

    onSave({
      name: form.name.trim(),
      legal_name: form.legal_name.trim(),
      partner_types: [form.partner_type],
      specialities: [form.speciality],
      services: [form.service],
      country: form.country.trim(),
      city: form.city.trim(),
      official_url: form.official_url.trim(),
      professional_body: form.professional_body.trim(),
      registration_number: form.registration_number.trim(),
      registry_url: form.registry_url.trim(),
      languages,
      description: form.description.trim(),
      priority: form.priority,
      professional_status: "unverified",
      relationship_stage: "under_review",
      why_relevant: form.why_relevant.trim(),
      contacts: [
        {
          name: form.contact_name.trim(),
          job_title: form.job_title.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          source_url:
            form.contact_source.trim() ||
            form.official_url.trim(),
          verification_status: "unverified",
        },
      ],
      sources: [
        {
          url:
            form.contact_source.trim() ||
            form.official_url.trim(),
          type: "public_professional_source",
        },
      ],
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/65 p-4">
      <div className="mx-auto my-6 max-w-5xl bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-[#A77E35]">
              {t(
                "legalPartners.form.eyebrow",
                "Verified professional profile"
              )}
            </p>
            <h2 className="mt-1 font-serif text-3xl text-[#0B2545]">
              {t(
                "legalPartners.form.title",
                "Add Legal or Transaction Partner"
              )}
            </h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-2 text-slate-400 hover:text-slate-700"
            aria-label={t("common.close", "Close")}
          >
            <X size={22} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-8 p-6">
          <section>
            <h3 className="mb-4 font-serif text-xl text-[#0B2545]">
              {t(
                "legalPartners.form.professionalInformation",
                "Professional information"
              )}
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <label>
                <span className={labelClass}>
                  {t("legalPartners.form.name", "Partner or firm name")} *
                </span>
                <Input
                  required
                  value={form.name}
                  onChange={(event) =>
                    update("name", event.target.value)
                  }
                />
              </label>

              <label>
                <span className={labelClass}>
                  {t("legalPartners.form.legalName", "Legal name")}
                </span>
                <Input
                  value={form.legal_name}
                  onChange={(event) =>
                    update("legal_name", event.target.value)
                  }
                />
              </label>

              <label>
                <span className={labelClass}>
                  {t("legalPartners.form.partnerType", "Professional type")} *
                </span>
                <select
                  required
                  value={form.partner_type}
                  onChange={(event) =>
                    update("partner_type", event.target.value)
                  }
                  className={fieldClass}
                >
                  {(meta?.partner_types || []).map((item) => (
                    <option key={item} value={item}>
                      {titleCase(item)}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span className={labelClass}>
                  {t("legalPartners.form.speciality", "Speciality")} *
                </span>
                <select
                  required
                  value={form.speciality}
                  onChange={(event) =>
                    update("speciality", event.target.value)
                  }
                  className={fieldClass}
                >
                  {(meta?.specialities || []).map((item) => (
                    <option key={item} value={item}>
                      {titleCase(item)}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span className={labelClass}>
                  {t("legalPartners.form.service", "Primary service")} *
                </span>
                <select
                  required
                  value={form.service}
                  onChange={(event) =>
                    update("service", event.target.value)
                  }
                  className={fieldClass}
                >
                  {(meta?.services || []).map((item) => (
                    <option key={item} value={item}>
                      {titleCase(item)}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span className={labelClass}>
                  {t("legalPartners.form.priority", "Priority")}
                </span>
                <select
                  value={form.priority}
                  onChange={(event) =>
                    update("priority", event.target.value)
                  }
                  className={fieldClass}
                >
                  {(meta?.priorities || ["A", "B", "C"]).map(
                    (item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    )
                  )}
                </select>
              </label>

              <label>
                <span className={labelClass}>
                  {t("legalPartners.form.country", "Country")} *
                </span>
                <Input
                  required
                  value={form.country}
                  onChange={(event) =>
                    update("country", event.target.value)
                  }
                />
              </label>

              <label>
                <span className={labelClass}>
                  {t("legalPartners.form.city", "City")}
                </span>
                <Input
                  value={form.city}
                  onChange={(event) =>
                    update("city", event.target.value)
                  }
                />
              </label>

              <label className="md:col-span-2">
                <span className={labelClass}>
                  {t("legalPartners.form.website", "Official website")} *
                </span>
                <Input
                  required
                  type="url"
                  value={form.official_url}
                  onChange={(event) =>
                    update("official_url", event.target.value)
                  }
                />
              </label>

              <label>
                <span className={labelClass}>
                  {t(
                    "legalPartners.form.professionalBody",
                    "Professional body"
                  )}
                </span>
                <Input
                  value={form.professional_body}
                  onChange={(event) =>
                    update("professional_body", event.target.value)
                  }
                />
              </label>

              <label>
                <span className={labelClass}>
                  {t(
                    "legalPartners.form.registrationNumber",
                    "Registration number"
                  )}
                </span>
                <Input
                  value={form.registration_number}
                  onChange={(event) =>
                    update("registration_number", event.target.value)
                  }
                />
              </label>

              <label>
                <span className={labelClass}>
                  {t("legalPartners.form.registryUrl", "Registry URL")}
                </span>
                <Input
                  type="url"
                  value={form.registry_url}
                  onChange={(event) =>
                    update("registry_url", event.target.value)
                  }
                />
              </label>

              <label>
                <span className={labelClass}>
                  {t(
                    "legalPartners.form.languages",
                    "Languages (comma separated)"
                  )}
                </span>
                <Input
                  value={form.languages}
                  onChange={(event) =>
                    update("languages", event.target.value)
                  }
                />
              </label>

              <label className="md:col-span-2">
                <span className={labelClass}>
                  {t("legalPartners.form.description", "Description")}
                </span>
                <textarea
                  rows={4}
                  value={form.description}
                  onChange={(event) =>
                    update("description", event.target.value)
                  }
                  className="w-full border border-slate-200 p-3 text-sm outline-none focus:border-[#C5A059]"
                />
              </label>
            </div>
          </section>

          <section className="border-t border-slate-200 pt-7">
            <h3 className="mb-4 font-serif text-xl text-[#0B2545]">
              {t(
                "legalPartners.form.professionalContact",
                "Public professional contact"
              )}
            </h3>
            <div className="grid gap-4 md:grid-cols-2">
              <label>
                <span className={labelClass}>
                  {t(
                    "legalPartners.form.contactName",
                    "Contact name or department"
                  )} *
                </span>
                <Input
                  required
                  value={form.contact_name}
                  onChange={(event) =>
                    update("contact_name", event.target.value)
                  }
                />
              </label>

              <label>
                <span className={labelClass}>
                  {t("legalPartners.form.jobTitle", "Position")}
                </span>
                <Input
                  value={form.job_title}
                  onChange={(event) =>
                    update("job_title", event.target.value)
                  }
                />
              </label>

              <label>
                <span className={labelClass}>
                  {t("legalPartners.form.email", "Professional email")} *
                </span>
                <Input
                  required
                  type="email"
                  value={form.email}
                  onChange={(event) =>
                    update("email", event.target.value)
                  }
                />
              </label>

              <label>
                <span className={labelClass}>
                  {t("legalPartners.form.phone", "Telephone")} *
                </span>
                <Input
                  required
                  type="tel"
                  value={form.phone}
                  onChange={(event) =>
                    update("phone", event.target.value)
                  }
                />
              </label>

              <label className="md:col-span-2">
                <span className={labelClass}>
                  {t(
                    "legalPartners.form.contactSource",
                    "Public contact source URL"
                  )} *
                </span>
                <Input
                  required
                  type="url"
                  value={form.contact_source}
                  onChange={(event) =>
                    update("contact_source", event.target.value)
                  }
                />
              </label>

              <label className="md:col-span-2">
                <span className={labelClass}>
                  {t(
                    "legalPartners.form.relevance",
                    "Why this professional is relevant"
                  )}
                </span>
                <textarea
                  rows={3}
                  value={form.why_relevant}
                  onChange={(event) =>
                    update("why_relevant", event.target.value)
                  }
                  className="w-full border border-slate-200 p-3 text-sm outline-none focus:border-[#C5A059]"
                />
              </label>
            </div>
          </section>

          <div className="border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
            {t(
              "legalPartners.form.privacyNotice",
              "Store public professional information only. Never enter privileged client communications or confidential case details."
            )}
          </div>

          <div className="flex justify-end gap-3 border-t border-slate-200 pt-5">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="rounded-none"
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="submit"
              disabled={saving}
              className="rounded-none bg-[#0B2545] px-7 hover:bg-[#12345e]"
            >
              {saving
                ? t("legalPartners.form.saving", "Saving…")
                : t("legalPartners.form.save", "Save partner")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
